import type { ConsentAction, ConsentState, OrbitConsentChange, OrbitDebugEvent, OrbitDebugType } from './types';

type DebugListener = (event: OrbitDebugEvent) => void;
type ChangeListener = (change: OrbitConsentChange) => void;

const debugListeners = new Set<DebugListener>();
const changeListeners = new Set<ChangeListener>();

let debugEnabled = false;

export function setDebugEnabled(enabled: boolean): void {
  debugEnabled = enabled;
}

export function isDebugEnabled(): boolean {
  return debugEnabled;
}

/**
 * Narrates what the runtime is doing. Silent unless data-debug is set, which is
 * how the portal playground shows why each integration fired or stayed blocked.
 */
export function emitDebug(
  type: OrbitDebugType,
  message: string,
  detail?: Record<string, unknown>,
): void {
  if (!debugEnabled) return;

  const event: OrbitDebugEvent = { type, at: new Date().toISOString(), message, detail };

  for (const listener of debugListeners) {
    try {
      listener(event);
    } catch {
      // A broken listener must not stop the runtime.
    }
  }

  dispatch('orbit:debug', event);
}

export function onDebug(listener: DebugListener): () => void {
  debugListeners.add(listener);
  return () => debugListeners.delete(listener);
}

export function emitConsentChange(consent: ConsentState, action: ConsentAction, revoked: string[]): void {
  const change: OrbitConsentChange = { consent, action, revoked };

  for (const listener of changeListeners) {
    try {
      listener(change);
    } catch {
      // Ignore listener failures.
    }
  }

  dispatch('orbit:consent', change);
}

export function onConsentChange(listener: ChangeListener): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function offConsentChange(listener: ChangeListener): void {
  changeListeners.delete(listener);
}

export function offDebug(listener: DebugListener): void {
  debugListeners.delete(listener);
}

function dispatch(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // CustomEvent unavailable (very old browsers), listeners still fire.
  }
}
